import { useCallback, useEffect, useState } from 'react';

// Note: The Prelude SDK needs to be dynamically imported to avoid SSR issues
// and because it requires browser APIs

interface PreludeState {
  dispatchId: string | null;
  isLoading: boolean;
  error: Error | null;
}

interface UsePreludeReturn extends PreludeState {
  verifySilent: (requestUrl: string) => Promise<string>;
  isPreludeEnabled: boolean;
}

const PRELUDE_SDK_KEY = process.env.NEXT_PUBLIC_PRELUDE_SDK_KEY;

export function usePrelude(): UsePreludeReturn {
  const [state, setState] = useState<PreludeState>({
    dispatchId: null,
    isLoading: false,
    error: null,
  });

  const isPreludeEnabled = Boolean(PRELUDE_SDK_KEY);

  useEffect(() => {
    if (!isPreludeEnabled || typeof window === 'undefined') {
      return;
    }

    let isMounted = true;

    async function initPrelude() {
      setState((prev) => ({ ...prev, isLoading: true }));

      try {
        // Dynamic import to avoid SSR issues
        // The SDK exports dispatchSignals as a standalone function
        const { dispatchSignals, Features } = await import('@prelude.so/js-sdk');

        const dispatchId = await dispatchSignals(PRELUDE_SDK_KEY!, {
          implementedFeatures: [Features.SilentVerification],
        });

        if (isMounted) {
          setState({
            dispatchId,
            isLoading: false,
            error: null,
          });
        }
      } catch (error) {
        console.error('Failed to initialize Prelude SDK', error);
        if (isMounted) {
          setState({
            dispatchId: null,
            isLoading: false,
            error: error instanceof Error ? error : new Error('Failed to initialize Prelude'),
          });
        }
      }
    }

    initPrelude();

    return () => {
      isMounted = false;
    };
  }, [isPreludeEnabled]);

  const verifySilent = useCallback(
    async (requestUrl: string): Promise<string> => {
      if (!isPreludeEnabled) {
        throw new Error('Prelude SDK is not enabled');
      }

      try {
        // The SDK exports performSilentVerification as a standalone function
        const { performSilentVerification } = await import('@prelude.so/js-sdk');
        const verificationCode = await performSilentVerification(requestUrl);
        return verificationCode;
      } catch (error) {
        console.error('Silent verification failed', error);
        throw error;
      }
    },
    [isPreludeEnabled],
  );

  return {
    ...state,
    verifySilent,
    isPreludeEnabled,
  };
}
